import { z } from "zod";
import { fileKindOf, OUTPUT_FILE_KINDS, type OutputFileKind } from "./processes";
import {
  amountValueSchema,
  dateValueSchema,
  OUTPUT_VALUE_KINDS,
  type AmountValue,
} from "./value-types";

/**
 * One field a person fills in (decision `2026-11-03-an-ask-names-the-output-it-fills`): a value of
 * one output value kind, or a file of one output file kind. A step's outputs and Starts when's
 * fields declare these shapes already; an ask names one of them, and every card that collects one
 * shows the same input for it. Browser-safe: the web's field component and the API's checks read
 * the same functions.
 */

const choiceText = z.string().trim().min(1).max(200);

/** What a field takes: the declared shape, without what only a case's outputs need (`per`). */
export const fieldShapeSchema = z.discriminatedUnion("type", [
  z
    .strictObject({
      type: z.literal("value"),
      kind: z.enum(OUTPUT_VALUE_KINDS),
      /** Only for a choice, which needs them. */
      choices: z
        .array(choiceText)
        .min(1)
        .max(50)
        .refine((choices) => new Set(choices).size === choices.length)
        .optional(),
      /** Only for an amount: when set, an amount in another currency is refused. */
      currency: amountValueSchema.shape.currency.optional(),
    })
    .refine((shape) => (shape.kind === "choice") === (shape.choices !== undefined))
    .refine((shape) => shape.currency === undefined || shape.kind === "amount"),
  z.strictObject({ type: z.literal("file"), fileKind: z.enum(OUTPUT_FILE_KINDS) }),
]);
export type FieldShape = z.infer<typeof fieldShapeSchema>;
export type ValueFieldShape = Extract<FieldShape, { type: "value" }>;

/**
 * A value as a field gives it, in the form a value output holds: text (a date as `YYYY-MM-DD`, a
 * choice as its words), a number, yes or no, or an amount in minor units with its currency.
 */
export const fieldValueSchema = z.union([
  z
    .string()
    .trim()
    .min(1)
    .max(2000)
    .refine((value) => !value.includes("\0")),
  z.number().finite(),
  z.boolean(),
  amountValueSchema,
]);
export type FieldValue = z.infer<typeof fieldValueSchema>;

/** Whether a value is of its shape's kind: a choice one of its choices, an amount in its currency. */
export function valueFitsField(shape: ValueFieldShape, value: FieldValue): boolean {
  switch (shape.kind) {
    case "text":
      return typeof value === "string";
    case "number":
      return typeof value === "number";
    case "yesNo":
      return typeof value === "boolean";
    case "date":
      return typeof value === "string" && dateValueSchema.safeParse(value).success;
    case "choice":
      return typeof value === "string" && (shape.choices ?? []).includes(value);
    case "amount": {
      const amount = amountValueSchema.safeParse(value);
      return (
        amount.success && (shape.currency === undefined || amount.data.currency === shape.currency)
      );
    }
  }
}

/** Whether a file's media type is of the field's kind; `any` takes every file the chat accepts. */
export const fileFitsField = (kind: OutputFileKind, mediaType: string): boolean =>
  kind === "any" || fileKindOf(mediaType) === kind;

/** The digits after the point an amount in `currency` has, as Intl knows the currency; else two. */
export function currencyDigits(currency: string): number {
  try {
    return (
      new Intl.NumberFormat("en-US", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/** An amount as a person reads it: "$1,648.00", "€12.50", "¥3,000". */
export function formatAmount({ minor, currency }: AmountValue): string {
  const digits = currencyDigits(currency);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(minor / 10 ** digits);
  } catch {
    return `${currency} ${(minor / 10 ** digits).toFixed(digits)}`;
  }
}

/** A date as a person reads it, in US order: "Thu, Nov 5, 2026". Read as a calendar day, not a time. */
export function formatDate(date: string): string {
  const day = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(day.getTime())) return date;
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(day);
}

/** A field's value in words, as the answer's message says it. */
export function fieldValueText(shape: ValueFieldShape, value: FieldValue): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return new Intl.NumberFormat("en-US").format(value);
  if (typeof value === "object") return formatAmount(value);
  return shape.kind === "date" ? formatDate(value) : value;
}

/** What a file kind takes, in words: "a PDF", "an image", "any file". */
export const FILE_KIND_WORDS: Readonly<Record<OutputFileKind, string>> = {
  pdf: "a PDF",
  image: "an image",
  spreadsheet: "a spreadsheet",
  document: "a document",
  any: "any file",
};
